import { useMemo } from 'react';
import { useTranslation } from '@/i18n/useTranslation';
import type { WizardAnswers } from '@/context/WizardSessionContext';
import WizardField from '@/components/wizard/WizardField';
import WizardInput from '@/components/wizard/WizardInput';
import WizardSelect from '@/components/wizard/WizardSelect';
import wizardForm from '@/styles/wizard.module.css';
import styles from './ContactStep.module.css';

export type ContactErrors = {
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
};

export interface ContactStepProps {
  answers: WizardAnswers;
  errors: ContactErrors;
  onChange: (patch: Partial<WizardAnswers>) => void;
}

const PHONE_PREFIXES = ['+972', '+970', '+1', '+44', '+49'] as const;

function splitPhone(full?: string, prefix?: string): { prefix: string; local: string } {
  const defaultPrefix = prefix ?? '+972';
  const value = (full ?? '').trim();
  if (!value) return { prefix: defaultPrefix, local: '' };

  const matched = PHONE_PREFIXES.find((p) => value.startsWith(p));
  if (matched) {
    return { prefix: matched, local: value.slice(matched.length).trim() };
  }
  return { prefix: defaultPrefix, local: value };
}

function joinPhone(prefix: string, local: string): string {
  const trimmed = local.trim();
  if (!trimmed) return '';
  return `${prefix} ${trimmed}`;
}

export default function ContactStep({ answers, errors, onChange }: ContactStepProps) {
  const t = useTranslation();
  const { prefix, local } = useMemo(
    () => splitPhone(answers.contact_phone, answers.contact_phone_prefix),
    [answers.contact_phone, answers.contact_phone_prefix],
  );

  const prefixOptions = PHONE_PREFIXES.map((value) => ({ value, label: value }));

  return (
    <section className={wizardForm.formFields} data-testid="contact-step">
      <WizardField
        label={t.wizard.fields.contactName}
        required
        error={errors.contact_name}
        testId="contact-name-field"
      >
        <WizardInput
          placeholder={t.wizard.fields.contactNamePlaceholder}
          value={answers.contact_name ?? ''}
          onChange={(value) => onChange({ contact_name: value })}
          hasError={Boolean(errors.contact_name)}
          autoComplete="name"
          data-testid="contact-name-input"
          aria-label={t.wizard.fields.contactName}
        />
      </WizardField>

      <WizardField
        label={t.wizard.fields.contactPhone}
        required
        error={errors.contact_phone}
        testId="contact-phone-field"
      >
        <div className={styles.phoneRow}>
          <div className={styles.prefixField} data-testid="contact-phone-prefix">
            <WizardSelect
              value={prefix}
              options={prefixOptions}
              onChange={(nextPrefix) => {
                onChange({
                  contact_phone_prefix: nextPrefix,
                  contact_phone: joinPhone(nextPrefix, local),
                });
              }}
              aria-label={t.wizard.fields.contactPhonePrefix}
              data-testid="contact-phone-prefix-select"
            />
          </div>
          <div className={styles.phoneField}>
            <WizardInput
              type="tel"
              dir="ltr"
              inputMode="tel"
              autoComplete="tel-national"
              placeholder={t.wizard.fields.contactPhonePlaceholder}
              value={local}
              onChange={(value) =>
                onChange({
                  contact_phone_prefix: prefix,
                  contact_phone: joinPhone(prefix, value),
                })
              }
              hasError={Boolean(errors.contact_phone)}
              data-testid="contact-phone-input"
              aria-label={t.wizard.fields.contactPhone}
            />
          </div>
        </div>
      </WizardField>

      <WizardField
        label={t.wizard.fields.contactEmail}
        required
        error={errors.contact_email}
        testId="contact-email-field"
      >
        <WizardInput
          type="email"
          dir="ltr"
          inputMode="email"
          autoComplete="email"
          placeholder={t.wizard.fields.contactEmailPlaceholder}
          value={answers.contact_email ?? ''}
          onChange={(value) => onChange({ contact_email: value })}
          hasError={Boolean(errors.contact_email)}
          data-testid="contact-email-input"
          aria-label={t.wizard.fields.contactEmail}
        />
      </WizardField>
    </section>
  );
}
